// BLOKAS 1 NotificationsSection.tsx: IMPORTAI
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, Info, AlertCircle, ChevronRight, Mail, Globe, Smartphone, Volume2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import { buildPatchFromChange } from '../../utils/patchMap';
import { useAuth } from '../../contexts/AuthContext'; // Keep this

// BLOKAS 2 NotificationsSection.tsx: INTERFACE'AI
interface NotificationsSectionProps {
  botId?: string;
  emailNotifications: boolean;
  onEmailNotificationsChange: (value: boolean) => void;
  webNotifications: boolean;
  onWebNotificationsChange: (value: boolean) => void;
  mobileNotifications: boolean;
  onMobileNotificationsChange: (value: boolean) => void;
  notifyOn: {
    trades: boolean;
    profitLoss: boolean;
    errors: boolean;
    signals: boolean;
    marketUpdates: boolean;
    systemUpdates: boolean;
  };
  onNotifyOnChange: (value: any) => void;
  emailFrequency: string;
  onEmailFrequencyChange: (value: string) => void;
  soundAlerts: boolean;
  onSoundAlertsChange: (value: boolean) => void;
}

// BLOKAS 2.1 NotificationsSection.tsx: TOOLTIP PROPS INTERFACE
interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
}

// BLOKAS 3 NotificationsSection.tsx: TOOLTIP KOMPONENTAS
const Tooltip: React.FC<TooltipProps> = ({ content, children }) => (
  <div className="group relative inline-block">
    {children}
    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-96 hidden group-hover:block z-50">
      <div className="bg-gray-900 text-white text-sm rounded-lg p-4 shadow-xl">
        {content}
        <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-gray-900 transform rotate-45"></div>
      </div>
    </div>
  </div>
);

// BLOKAS 4 NotificationsSection.tsx: NOTIFICATIONSSECTION KOMPONENTAS
export const NotificationsSection: React.FC<NotificationsSectionProps> = ({
  botId,
  emailNotifications,
  onEmailNotificationsChange,
  webNotifications,
  onWebNotificationsChange,
  mobileNotifications,
  onMobileNotificationsChange,
  notifyOn,
  onNotifyOnChange,
  emailFrequency,
  onEmailFrequencyChange,
  soundAlerts,
  onSoundAlertsChange,
}) => {
  console.log(`🔧 [BLOKAS 4 NotificationsSection.tsx] Komponentas inicijuotas su botId:`, botId);

  // BLOKAS 4.1 NotificationsSection.tsx: AUTH KONTEKSTAS
  const { user } = useAuth();
  console.log(`🔧 [BLOKAS 4.1 NotificationsSection.tsx] Gautas vartotojo kontekstas:`, user);

  // BLOKAS 4.2 NotificationsSection.tsx: UNIFIKUOTAS KEITIMŲ VALDIKLIS
  const handleChange = (field: string, value: any) => {
    console.log(`🔔 [BLOKAS 4.2 NotificationsSection.tsx] 📤 UNIFIKUOTAS KEITIMŲ VALDIKLIS:`, {
      field,
      newValue: value,
      type: typeof value,
      botId: botId,
      timestamp: new Date().toISOString()
    });
    
    // BLOKAS 4.2.1 NotificationsSection.tsx: SPECIFINIŲ VALDIKLIŲ IŠKVIETIMAS
    switch (field) {
      case 'emailNotifications':
        onEmailNotificationsChange(value);
        // Use global sendChange if available
        if (window.sendChange) {
          window.sendChange('emailNotifications', value);
        }
        break;
      case 'webNotifications':
        onWebNotificationsChange(value);
        // Use global sendChange if available
        if (window.sendChange) {
          window.sendChange('webNotifications', value);
        }
        break;
      case 'mobileNotifications':
        onMobileNotificationsChange(value);
        // Use global sendChange if available
        if (window.sendChange) {
          window.sendChange('mobileNotifications', value);
        }
        break;
      case 'notifyOn':
        onNotifyOnChange(value);
        // Use global sendChange if available
        if (window.sendChange) {
          window.sendChange('notifyOn', value);
        }
        break;
      case 'emailFrequency':
        onEmailFrequencyChange(value);
        // Use global sendChange if available
        if (window.sendChange) {
          window.sendChange('emailFrequency', value);
        }
        break;
      case 'soundAlerts':
        onSoundAlertsChange(value);
        // Use global sendChange if available
        if (window.sendChange) {
          window.sendChange('soundAlerts', value);
        }
        break;
    }

    console.log(`🔔 [BLOKAS 4.2.1 NotificationsSection.tsx] Laukas apdorotas:`, { field, value });
  };

  // BLOKAS 4.3 NotificationsSection.tsx: NOTIFY ON KEITIMŲ VALDIKLIS
  const handleNotifyOnChange = (key: string, value: boolean) => {
    console.log(`🔔 [BLOKAS 4.3 NotificationsSection.tsx] NotifyOn keičiamas:`, { key, value });
    const newNotifyOn = {
      ...notifyOn,
      [key]: value
    };
    handleChange('notifyOn', newNotifyOn);
  };

  // BLOKAS 4.4 NotificationsSection.tsx: TOOLTIP TURINYS - PRANEŠIMŲ TIPAI
  const NotificationTypesTooltip = (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-3 h-3 rounded-full bg-blue-400"></div>
          <h5 className="font-medium text-blue-400">Notification Types</h5>
        </div>
        <div className="space-y-3">
          <div>
            <p className="font-medium text-blue-400 mb-1">Trading Events</p>
            <ul className="space-y-1 text-gray-300">
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                Trades: Buy/sell order executions
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                Profit/Loss: Position closures with P&L
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                Signals: Strategy entry/exit signals
              </li>
            </ul>
          </div>

          <div>
            <p className="font-medium text-blue-400 mb-1">System Events</p>
            <ul className="space-y-1 text-gray-300">
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                Errors: Bot errors and failures
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                Market Updates: Price alerts and news
              </li>
              <li className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                System Updates: Platform updates
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="flex items-start gap-2 bg-blue-900/20 p-2 rounded">
        <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-blue-300 text-xs">
          Choose which events trigger notifications. You can customize delivery methods separately for each notification type.
        </p>
      </div>
    </div>
  );

  // BLOKAS 4.5 NotificationsSection.tsx: TOOLTIP TURINYS - EL. PAŠTO DAŽNUMAS
  const EmailFrequencyTooltip = (
    <div className="space-y-4">
      <div>
        <h5 className="font-medium text-green-400 mb-2">Email Frequency Options</h5>
        <div className="space-y-3 text-sm text-gray-300">
          <div className="flex items-start gap-2">
            <div className="flex-shrink-0 mt-1">
              <div className="w-5 h-5 rounded-full bg-green-900/30 flex items-center justify-center">
                <span className="text-green-400">1</span>
              </div>
            </div>
            <div>
              <p className="font-medium text-green-400">Instant</p>
              <ul className="mt-1 space-y-1 list-disc ml-4">
                <li>Immediate email for each event</li>
                <li>Best for active traders</li>
                <li>Highest notification volume</li>
              </ul>
            </div>
          </div>
          
          <div className="flex items-start gap-2">
            <div className="flex-shrink-0 mt-1">
              <div className="w-5 h-5 rounded-full bg-green-900/30 flex items-center justify-center">
                <span className="text-green-400">2</span>
              </div>
            </div>
            <div>
              <p className="font-medium text-green-400">Daily</p>
              <ul className="mt-1 space-y-1 list-disc ml-4">
                <li>One summary email per day</li>
                <li>Balanced approach</li>
                <li>Good for swing traders</li>
              </ul>
            </div>
          </div>
          
          <div className="flex items-start gap-2">
            <div className="flex-shrink-0 mt-1">
              <div className="w-5 h-5 rounded-full bg-green-900/30 flex items-center justify-center">
                <span className="text-green-400">3</span>
              </div>
            </div>
            <div>
              <p className="font-medium text-green-400">Weekly</p>
              <ul className="mt-1 space-y-1 list-disc ml-4">
                <li>Weekly performance summary</li>
                <li>Minimal email volume</li>
                <li>Good for long-term investors</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-start gap-2 bg-green-900/20 p-2 rounded">
        <AlertCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
        <p className="text-green-300 text-xs">
          Choose frequency based on your trading style and how actively you want to monitor your bots.
        </p>
      </div>
    </div>
  );

  console.log(`🔧 [BLOKAS 4.6 NotificationsSection.tsx] JSX renderinimas pradėtas`);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      {/* BLOKAS 4.7 NotificationsSection.tsx: PAGRINDINIS KONTEINERIS */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Notifications</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Configure how and when you receive notifications</p>
          </div>
          <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
            <Bell className="h-6 w-6 text-blue-500" />
          </div>
        </div>

        <div className="space-y-8">
          {/* BLOKAS 4.8 NotificationsSection.tsx: PRANEŠIMŲ KANALAI */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notification Channels</h3>
            
            {/* BLOKAS 4.8.1 NotificationsSection.tsx: EL. PAŠTO PRANEŠIMAI */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Notifications
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Receive notifications via email
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleChange('emailNotifications', !emailNotifications)}
                className={cn(
                  "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                  emailNotifications ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
                )}
              >
                <span
                  className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                    emailNotifications ? "translate-x-6" : "translate-x-1"
                  )}
                />
              </button>
            </div>

            {/* BLOKAS 4.8.2 NotificationsSection.tsx: ŽINIATINKLIO PRANEŠIMAI */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Globe className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Web Notifications
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Browser push notifications
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleChange('webNotifications', !webNotifications)}
                className={cn(
                  "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                  webNotifications ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
                )}
              >
                <span
                  className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                    webNotifications ? "translate-x-6" : "translate-x-1"
                  )}
                />
              </button>
            </div>

            {/* BLOKAS 4.8.3 NotificationsSection.tsx: MOBILIEJI PRANEŠIMAI */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Smartphone className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Mobile Notifications
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Mobile app push notifications
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleChange('mobileNotifications', !mobileNotifications)}
                className={cn(
                  "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                  mobileNotifications ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
                )}
              >
                <span
                  className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                    mobileNotifications ? "translate-x-6" : "translate-x-1"
                  )}
                />
              </button>
            </div>

            {/* BLOKAS 4.8.4 NotificationsSection.tsx: GARSO SIGNALAI */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Volume2 className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Sound Alerts
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Audio notifications for important events
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleChange('soundAlerts', !soundAlerts)}
                className={cn(
                  "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                  soundAlerts ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
                )}
              >
                <span
                  className={cn(
                    "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                    soundAlerts ? "translate-x-6" : "translate-x-1"
                  )}
                />
              </button>
            </div>
          </div>

          {/* BLOKAS 4.9 NotificationsSection.tsx: EL. PAŠTO DAŽNUMAS */}
          {emailNotifications && (
            <div className="space-y-4 animate-in fade-in duration-200">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email Frequency
                </label>
                <Tooltip content={EmailFrequencyTooltip}>
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </Tooltip>
              </div>
              <select
                value={emailFrequency}
                onChange={(e) => handleChange('emailFrequency', e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="instant">Instant</option>
                <option value="daily">Daily Summary</option>
                <option value="weekly">Weekly Summary</option>
              </select>
            </div>
          )}

          {/* BLOKAS 4.10 NotificationsSection.tsx: PRANEŠIMŲ TIPAI */}
          <div className="space-y-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notification Types</h3>
              <Tooltip content={NotificationTypesTooltip}>
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </Tooltip>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* BLOKAS 4.10.1 NotificationsSection.tsx: PREKYBOS OPERACIJOS */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Trade Executions
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Buy and sell order notifications
                  </p>
                </div>
                <button
                  onClick={() => handleNotifyOnChange('trades', !notifyOn.trades)}
                  className={cn(
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                    notifyOn.trades ? "bg-green-500" : "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      notifyOn.trades ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>

              {/* BLOKAS 4.10.2 NotificationsSection.tsx: PELNAS/STRATA */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Profit & Loss
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Position P&L updates
                  </p>
                </div>
                <button
                  onClick={() => handleNotifyOnChange('profitLoss', !notifyOn.profitLoss)}
                  className={cn(
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                    notifyOn.profitLoss ? "bg-green-500" : "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      notifyOn.profitLoss ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>

              {/* BLOKAS 4.10.3 NotificationsSection.tsx: KLAIDOS */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Errors & Alerts
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Bot errors and system alerts
                  </p>
                </div>
                <button
                  onClick={() => handleNotifyOnChange('errors', !notifyOn.errors)}
                  className={cn(
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                    notifyOn.errors ? "bg-red-500" : "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      notifyOn.errors ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>

              {/* BLOKAS 4.10.4 NotificationsSection.tsx: SIGNALAI */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Trading Signals
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Strategy entry/exit signals
                  </p>
                </div>
                <button
                  onClick={() => handleNotifyOnChange('signals', !notifyOn.signals)}
                  className={cn(
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                    notifyOn.signals ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      notifyOn.signals ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>

              {/* BLOKAS 4.10.5 NotificationsSection.tsx: RINKOS ATNAUJINIMAI */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Market Updates
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Price alerts and market news
                  </p>
                </div>
                <button
                  onClick={() => handleNotifyOnChange('marketUpdates', !notifyOn.marketUpdates)}
                  className={cn(
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                    notifyOn.marketUpdates ? "bg-purple-500" : "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      notifyOn.marketUpdates ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>

              {/* BLOKAS 4.10.6 NotificationsSection.tsx: SISTEMOS ATNAUJINIMAI */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    System Updates
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Platform updates and maintenance
                  </p>
                </div>
                <button
                  onClick={() => handleNotifyOnChange('systemUpdates', !notifyOn.systemUpdates)}
                  className={cn(
                    "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
                    notifyOn.systemUpdates ? "bg-orange-500" : "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                      notifyOn.systemUpdates ? "translate-x-6" : "translate-x-1"
                    )}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* BLOKAS 4.11 NotificationsSection.tsx: SUMMA */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800/30">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-400 mb-1">
                  Notification Summary
                </p>
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  <p>• Email: {emailNotifications ? `Enabled (${emailFrequency})` : 'Disabled'}</p>
                  <p>• Web: {webNotifications ? 'Enabled' : 'Disabled'}</p>
                  <p>• Mobile: {mobileNotifications ? 'Enabled' : 'Disabled'}</p>
                  <p>• Sound: {soundAlerts ? 'Enabled' : 'Disabled'}</p>
                  <p>• Active notification types: {Object.values(notifyOn).filter(Boolean).length}/6</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default NotificationsSection;